import { useState } from "react";
import TextInput from "./TextInput";
import { getIn, useFormikContext } from "formik";
import Button from "./Button";
import useDebouncedValue from "../../hooks/use-debounced-value";
import { ReactSelectStandalone } from "./SelectStandalone";
import useSiretSearch from "../../hooks/use-siret-search";

function buildLabel(suggestion) {
  if (!suggestion) return "";
  const parts = [suggestion.nom];
  if (suggestion.siret) parts.push(`(${suggestion.siret})`);
  if (suggestion.ville) parts.push(`- ${suggestion.ville}`);
  return parts.join(" ");
}

export default function SiretFields({
  namePrefix = "",
  nameSiret: nameSiretProps = "siret",
  nameNomSociete: nameNomSocieteProps = "nom_societe",
  nameAdresse: nameAdresseProps = null,
  nameVille: nameVilleProps = null,
  nameCp: nameCpProps = null,
  nameNom: nameNomProps = null,
  namePrenom: namePrenomProps = null,
  nameDebutActivite: nameDebutActiviteProps = null,
  nameNumeroTva: nameNumeroTvaProps = null,
  nameSocType: nameSocTypeProps = null,
  mandatory = false,
  label = "SIRET / Nom de l'entreprise",
  placeholder = "12345678901234 ou Nom de l'entreprise",
}) {
  const { setFieldValue, values, errors, touched, setFieldTouched } =
    useFormikContext();
  const [query, setQuery] = useState("");
  const [showManualEntry, setShowManualEntry] = useState(false);
  const debouncedQuery = useDebouncedValue(query, 300);
  const { suggestions, isLoading: isLoadingSuggestions } =
    useSiretSearch(debouncedQuery);

  const nameSiret = `${namePrefix}${nameSiretProps}`;
  const nameNomSociete = `${namePrefix}${nameNomSocieteProps}`;
  const nameAdresse = nameAdresseProps
    ? `${namePrefix}${nameAdresseProps}`
    : null;
  const nameVille = nameVilleProps ? `${namePrefix}${nameVilleProps}` : null;
  const nameCp = nameCpProps ? `${namePrefix}${nameCpProps}` : null;
  const nameNom = nameNomProps ? `${namePrefix}${nameNomProps}` : null;
  const namePrenom = namePrenomProps ? `${namePrefix}${namePrenomProps}` : null;
  const nameDebutActivite = nameDebutActiviteProps
    ? `${namePrefix}${nameDebutActiviteProps}`
    : null;
  const nameNumeroTva = nameNumeroTvaProps
    ? `${namePrefix}${nameNumeroTvaProps}`
    : null;
  const nameSocType = nameSocTypeProps
    ? `${namePrefix}${nameSocTypeProps}`
    : null;

  const isInvalid =
    (getIn(errors, nameSiret) || getIn(errors, nameNomSociete)) &&
    (getIn(touched, nameSiret) || getIn(touched, nameNomSociete));

  async function suggestionSelectHandler(suggestion) {
    setQuery("");
    const updates = [
      setFieldValue(nameSiret, suggestion?.siret ?? ""),
      setFieldValue(nameNomSociete, suggestion?.nom ?? ""),
    ];
    if (nameAdresse && suggestion?.rue) {
      updates.push(setFieldValue(nameAdresse, suggestion.rue));
    }
    if (nameVille && suggestion?.ville) {
      updates.push(setFieldValue(nameVille, suggestion.ville));
    }
    if (nameCp && suggestion?.cp) {
      updates.push(setFieldValue(nameCp, suggestion.cp));
    }
    if (nameNom && suggestion?.dirigeantNom) {
      updates.push(setFieldValue(nameNom, suggestion.dirigeantNom));
    }
    if (namePrenom && suggestion?.dirigeantPrenom) {
      updates.push(setFieldValue(namePrenom, suggestion.dirigeantPrenom));
    }
    if (nameDebutActivite && suggestion?.dateDebutActivite) {
      updates.push(
        setFieldValue(nameDebutActivite, suggestion.dateDebutActivite),
      );
    }
    if (nameNumeroTva && suggestion?.numeroTva) {
      updates.push(setFieldValue(nameNumeroTva, suggestion.numeroTva));
    }
    if (nameSocType && suggestion?.socType) {
      updates.push(setFieldValue(nameSocType, suggestion.socType));
    }
    await Promise.all(updates);
    return blurSelectHandler();
  }

  function blurSelectHandler() {
    const touches = [
      setFieldTouched(nameSiret),
      setFieldTouched(nameNomSociete),
    ];
    if (nameAdresse) touches.push(setFieldTouched(nameAdresse));
    if (nameVille) touches.push(setFieldTouched(nameVille));
    if (nameCp) touches.push(setFieldTouched(nameCp));
    if (nameNom) touches.push(setFieldTouched(nameNom));
    if (namePrenom) touches.push(setFieldTouched(namePrenom));
    return Promise.all(touches);
  }

  let selectSuggestions = [...(suggestions || [])];
  let selectValue = "";
  const valueSiret = getIn(values, nameSiret);
  const valueNomSociete = getIn(values, nameNomSociete);
  if (valueSiret && valueNomSociete) {
    selectValue = {
      siret: valueSiret,
      nom: valueNomSociete,
    };
  }

  return (
    <>
      {!showManualEntry ? (
        <>
          <ReactSelectStandalone
            components={{ DropdownIndicator: null }}
            options={selectSuggestions}
            filterOption={null}
            value={selectValue}
            isClearable
            onBlur={blurSelectHandler}
            getOptionLabel={buildLabel}
            getOptionValue={(opt) => opt?.siret || ""}
            onInputChange={(value) => setQuery(value)}
            onChange={suggestionSelectHandler}
            loadingMessage={() => <>Chargement des suggestions</>}
            noOptionsMessage={({ inputValue }) =>
              inputValue.length > 2 ? <>Aucune entreprise trouvée</> : null
            }
            placeholder={placeholder}
            isLoading={isLoadingSuggestions}
            isInvalid={isInvalid}
            error={"Veuillez sélectionner une entreprise valide"}
            label={label}
            mandatory={mandatory}
          />
          <p className="secondaryText marginB10">
            L'entreprise n'apparait pas ?{" "}
            <Button
              type={"button"}
              variant="tertiary"
              onClick={() => setShowManualEntry(true)}
            >
              Entrer manuellement
            </Button>
          </p>
        </>
      ) : (
        <>
          <TextInput
            name={nameSiret}
            label={"Numéro SIRET"}
            placeholder={"12345678901234"}
            mandatory={mandatory}
          />
          <TextInput
            name={nameNomSociete}
            label={"Dénomination sociale"}
            placeholder={"SCI de la colline"}
            mandatory={mandatory}
          />
          <p className="secondaryText marginB10 marginT5">
            <Button
              type={"button"}
              variant="tertiary"
              onClick={() => setShowManualEntry(false)}
            >
              Rechercher dans l'annuaire
            </Button>
          </p>
        </>
      )}
    </>
  );
}
